import React, {
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { TranslationContext } from "../../contexts/TranslationContext";
import i18n from "../../i18n";
import {
  getStaticTranslation,
  normalizeLang,
} from "../../translations/registerStaticTranslations";

// =====================================================================
// InfoDot - the small "i" page explainer that sits next to a page title
// (8px gap, vertically centered by the parent's flex row). Hover (300ms
// delay) opens the popover unpinned; click pins it. Esc, outside click
// or re-click close it. Only ONE InfoDot is open at a time app-wide
// (CustomEvent 'tp-infodot-open' singleton). The 20px dot carries an
// invisible 44x44 touch target; on mobile a tap opens and a tap outside
// closes (no close button). The bubble avoids the viewport edges
// horizontally and flips above the trigger when there is no room below;
// the caret stays anchored to the trigger either way.
//
// Props:
//   title          - popover heading (English source text)
//   body           - popover body copy (English source text)
//   learnMoreRoute - optional in-app route; when absent NO footer link
//   learnMoreLabel - optional link label, default "Learn more"
//   ariaLabel      - optional trigger aria-label, default "About this page"
// =====================================================================

const OPEN_HOVER_DELAY_MS = 300;
const CLOSE_GRACE_MS = 150;
const EDGE_MARGIN_PX = 16;
const TRIGGER_OFFSET_PX = 8;
const CARET_HALF_PX = 5;
const SINGLETON_EVENT = "tp-infodot-open";

// Same cache-key scheme as useTranslate/TranslationContext (hash for >100
// chars), so the runtime-cache fallback below hits the identical entries.
const getCacheKey = (text, language) => {
  if (text.length > 100) {
    const hash = text.split("").reduce((a, b) => {
      a = (a << 5) - a + b.charCodeAt(0);
      return a & a;
    }, 0);
    return `${language}_${Math.abs(hash)}`;
  }
  return `${text}_${language}`;
};

const InfoDot = ({
  title,
  body,
  learnMoreRoute,
  learnMoreLabel = "Learn more",
  ariaLabel = "About this page",
}) => {
  // The popover renders its texts already translated instead of relying on
  // the DOM translator: the observer needs >2 added elements to fire, so a
  // popover opening never triggers a scan. We read the context directly
  // (repo pattern, see useUiLanguage.js / TranslationContext.js:8-14) so the
  // component also renders in unit tests without a provider — `useTranslation`
  // would throw there. Lookup order mirrors the DOM translator: static
  // bundle first (info-dot.json / info-dot-copy.json / the page-title
  // bundles), then the runtime machine-translation cache, then English.
  const translationContext = useContext(TranslationContext);
  const currentLanguage =
    (translationContext && translationContext.currentLanguage) || "en";
  const translationCache =
    (translationContext && translationContext.translationCache) || {};

  const t = (text) => {
    if (!text) return text;
    const lang = (currentLanguage || "en").toLowerCase();
    if (lang === "en") return text;
    const staticTx = getStaticTranslation(i18n, text, normalizeLang(lang));
    if (staticTx) return staticTx;
    return translationCache[getCacheKey(text, currentLanguage)] || text;
  };
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  // {placement: "bottom"|"top", left: px shift of the bubble relative to
  //  the trigger, caretLeft: px of the caret inside the bubble}
  const [pos, setPos] = useState(null);

  const instanceId = useId();
  const popoverId = `tp-infodot-${instanceId}`;

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);

  const clearOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };
  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openPopover = useCallback(
    (pin) => {
      clearOpenTimer();
      clearCloseTimer();
      setOpen(true);
      setPinned(pin);
      // Singleton: tell every other InfoDot to close.
      window.dispatchEvent(
        new CustomEvent(SINGLETON_EVENT, { detail: instanceId })
      );
    },
    [instanceId]
  );

  const closePopover = useCallback(() => {
    clearOpenTimer();
    clearCloseTimer();
    setOpen(false);
    setPinned(false);
    setPos(null);
  }, []);

  // ── Trigger interactions ────────────────────────────────────────────
  const handleTriggerClick = () => {
    clearOpenTimer();
    if (open && pinned) {
      closePopover();
      return;
    }
    if (open) {
      // Hover-opened: the click pins it.
      setPinned(true);
      return;
    }
    openPopover(true);
  };

  const handleTriggerKeyDown = (event) => {
    if (event.key === "Enter" || event.key === " ") {
      // preventDefault suppresses the native synthetic click, so the
      // toggle runs exactly once per key press.
      event.preventDefault();
      handleTriggerClick();
      return;
    }
    if (event.key === "Escape" && open) {
      event.preventDefault();
      closePopover();
    }
  };

  const handleTriggerMouseEnter = () => {
    clearCloseTimer();
    if (open || openTimerRef.current) return;
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      openPopover(false);
    }, OPEN_HOVER_DELAY_MS);
  };

  const handleTriggerMouseLeave = () => {
    clearOpenTimer();
    if (!open || pinned) return;
    // Grace delay so the pointer can travel across the 8px gap into the
    // bubble without the popover collapsing.
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      closePopover();
    }, CLOSE_GRACE_MS);
  };

  // ── Popover hover keeps an unpinned bubble alive ────────────────────
  const handlePopoverMouseEnter = () => {
    clearCloseTimer();
  };

  const handlePopoverMouseLeave = () => {
    if (pinned) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      closePopover();
    }, CLOSE_GRACE_MS);
  };

  // ── Singleton: a foreign InfoDot opened -> close this one ───────────
  useEffect(() => {
    const handleForeignOpen = (event) => {
      if (event.detail === instanceId) return;
      closePopover();
    };
    window.addEventListener(SINGLETON_EVENT, handleForeignOpen);
    return () => window.removeEventListener(SINGLETON_EVENT, handleForeignOpen);
  }, [instanceId, closePopover]);

  // ── While open: outside click closes, Esc closes + refocuses ────────
  useEffect(() => {
    if (!open) return undefined;
    const handleDocumentPointerDown = (event) => {
      if (rootRef.current && rootRef.current.contains(event.target)) return;
      closePopover();
    };
    const handleDocumentKeyDown = (event) => {
      if (event.key !== "Escape") return;
      closePopover();
      if (triggerRef.current) triggerRef.current.focus();
    };
    document.addEventListener("mousedown", handleDocumentPointerDown);
    document.addEventListener("touchstart", handleDocumentPointerDown);
    document.addEventListener("keydown", handleDocumentKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleDocumentPointerDown);
      document.removeEventListener("touchstart", handleDocumentPointerDown);
      document.removeEventListener("keydown", handleDocumentKeyDown);
    };
  }, [open, closePopover]);

  // ── Position: measure on open and on resize/scroll ──────────────────
  useLayoutEffect(() => {
    if (!open) return undefined;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      const popover = popoverRef.current;
      if (!trigger || !popover) return;
      const triggerRect = trigger.getBoundingClientRect();
      const popoverRect = popover.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const width =
        popoverRect.width ||
        Math.min(340, viewportWidth - 2 * EDGE_MARGIN_PX);
      const triggerCenter = triggerRect.left + triggerRect.width / 2;
      // Horizontal: center on the trigger, then clamp to the viewport
      // (16px margin each side). Only the bubble shifts - the caret
      // stays anchored over the trigger.
      const idealLeft = triggerCenter - width / 2;
      const maxLeft = Math.max(
        EDGE_MARGIN_PX,
        viewportWidth - EDGE_MARGIN_PX - width
      );
      const clampedLeft = Math.min(Math.max(idealLeft, EDGE_MARGIN_PX), maxLeft);
      // Vertical: below by default, flip above only when below does not
      // fit and above does.
      const fitsBelow =
        triggerRect.bottom + TRIGGER_OFFSET_PX + popoverRect.height <=
        viewportHeight;
      const fitsAbove =
        triggerRect.top - TRIGGER_OFFSET_PX - popoverRect.height >= 0;
      const placement = !fitsBelow && fitsAbove ? "top" : "bottom";
      const caretLeft = Math.min(
        Math.max(triggerCenter - clampedLeft - CARET_HALF_PX, 10),
        Math.max(width - 20, 10)
      );
      setPos({ placement, left: clampedLeft - triggerRect.left, caretLeft });
    };
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  // ── Clear pending timers on unmount ─────────────────────────────────
  useEffect(() => {
    return () => {
      clearOpenTimer();
      clearCloseTimer();
    };
  }, []);

  const placement = pos ? pos.placement : "bottom";

  const triggerStateClasses = open
    ? "bg-[#2283FF] text-white"
    : "bg-[#EEF1F5] text-[#6B7280] hover:bg-[#E8F1FF] hover:text-[#2283FF] focus-visible:bg-[#E8F1FF] focus-visible:text-[#2283FF]";

  return (
    <span ref={rootRef} className="relative inline-flex flex-none items-center">
      {/* data-no-translate: the aria-label is rendered already translated
          via t(), so the DOM translator must not touch it again. */}
      <button
        type="button"
        ref={triggerRef}
        aria-label={t(ariaLabel)}
        aria-expanded={open}
        aria-describedby={open ? popoverId : undefined}
        data-no-translate="true"
        onClick={handleTriggerClick}
        onKeyDown={handleTriggerKeyDown}
        onMouseEnter={handleTriggerMouseEnter}
        onMouseLeave={handleTriggerMouseLeave}
        className={`relative inline-flex h-5 w-5 flex-none cursor-pointer select-none items-center justify-center rounded-full text-[12px] font-semibold leading-none tracking-normal focus:outline-none focus-visible:shadow-[0_0_0_2px_rgba(34,131,255,0.35)] ${triggerStateClasses}`}
      >
        {/* Invisible 44x44 touch target; the visual dot stays 20px. */}
        <span aria-hidden="true" className="absolute -inset-3" />
        <span aria-hidden="true" data-no-translate="true">
          i
        </span>
      </button>
      {open && (
        <div
          id={popoverId}
          role="tooltip"
          ref={popoverRef}
          onMouseEnter={handlePopoverMouseEnter}
          onMouseLeave={handlePopoverMouseLeave}
          className="absolute z-50 box-border w-[340px] max-w-[calc(100vw-32px)] whitespace-normal text-left font-normal normal-case tracking-normal rounded-[12px] border border-[#E5E7EB] bg-white px-5 py-[18px] shadow-[0_12px_32px_rgba(17,24,39,0.10)]"
          style={{
            left: `${pos ? pos.left : 0}px`,
            ...(placement === "bottom"
              ? { top: `calc(100% + ${TRIGGER_OFFSET_PX}px)` }
              : { bottom: `calc(100% + ${TRIGGER_OFFSET_PX}px)` }),
          }}
        >
          {/* Caret: rotated square anchored over the trigger. */}
          <span
            aria-hidden="true"
            className={
              placement === "bottom"
                ? "absolute top-[-6px] h-[10px] w-[10px] rotate-45 border-l border-t border-[#E5E7EB] bg-white"
                : "absolute bottom-[-6px] h-[10px] w-[10px] rotate-45 border-b border-r border-[#E5E7EB] bg-white"
            }
            style={{ left: `${pos ? pos.caretLeft : 10}px` }}
          />
          {/* data-no-translate on each text container: the texts are rendered
              already translated via t(); the DOM translator (which checks the
              DIRECT parent of a text node) must not translate them twice. */}
          <div
            data-no-translate="true"
            className="mb-2 text-[15px] font-semibold leading-[1.3] tracking-[-0.1px] text-[#111827]"
          >
            {t(title)}
          </div>
          <div
            data-no-translate="true"
            className="text-[14px] font-normal leading-[1.6] text-[#4B5563]"
          >
            {t(body)}
          </div>
          {learnMoreRoute && (
            <Link
              to={learnMoreRoute}
              className="mt-3 inline-flex items-center gap-1 text-[13.5px] font-medium leading-none text-[#2283FF] no-underline hover:text-[#1B6FE0]"
            >
              <span data-no-translate="true">{t(learnMoreLabel)}</span>
              <span aria-hidden="true" data-no-translate="true">
                {"→"}
              </span>
            </Link>
          )}
        </div>
      )}
    </span>
  );
};

export default InfoDot;
